import { useCallback, useEffect, useState } from 'react';
import { fetchSteps, StepsScope, StepsState } from './steps-api';

export const RUNNING_INTERVAL = 2000;
export const IDLE_INTERVAL = 10000;

function messageOf(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}

// a job assignee who is not task staff can't see the task: for them the task has no steps
function isAbsent(error: unknown): boolean {
    const code = (error as { code?: unknown } | null)?.code;
    return code === 403 || code === 404;
}

export function useStepsPolling(taskId: number, scope: StepsScope = 'drawings'): {
    state: StepsState | null;
    error: string | null;
    absent: boolean;
    refresh(): void;
    replace(next: StepsState): void;
} {
    const [state, setState] = useState<StepsState | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [absent, setAbsent] = useState(false);
    const [generation, setGeneration] = useState(0);

    useEffect(() => {
        setState(null);
        setError(null);
        setAbsent(false);
    }, [taskId, scope]);

    useEffect(() => {
        if (absent) return undefined;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | null = null;
        async function load(): Promise<void> {
            let delay = IDLE_INTERVAL;
            try {
                const next = await fetchSteps(taskId, scope);
                if (cancelled) return;
                setState(next);
                setError(null);
                if (next.running) delay = RUNNING_INTERVAL;
            } catch (e) {
                if (cancelled) return;
                if (isAbsent(e)) {
                    setAbsent(true);
                    return;
                }
                setError(messageOf(e));
            }
            timer = setTimeout(load, delay);
        }
        load();
        return () => {
            cancelled = true;
            if (timer !== null) clearTimeout(timer);
        };
    }, [taskId, scope, generation, absent]);

    const refresh = useCallback(() => setGeneration((value) => value + 1), []);
    const replace = useCallback((next: StepsState) => {
        setState(next);
        setGeneration((value) => value + 1);
    }, []);

    return {
        state, error, absent, refresh, replace,
    };
}
